import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from './api';

const DEBOUNCE_MS = 700;

/**
 * Persist a single UI preference value to the backend per authenticated user.
 *
 * Behaviour:
 *  - Seeds the initial value from localStorage immediately (no flicker).
 *  - On mount, fetches the stored value from the backend and applies it if present.
 *  - On every change, writes to localStorage synchronously and debounces the
 *    backend PUT so rapid changes (e.g. clicking column pickers) only make one request.
 *
 * @param {string}  key           Unique preference key (e.g. 'item_browser_cat_cols')
 * @param {any}     defaultValue  Value to use when no preference is stored anywhere
 * @returns {[any, (newValue: any) => void]}
 */
interface PreferenceResponse<T> {
  value?: T;
}

export function useUserPreference<T>(key: string, defaultValue: T): [T, (newValue: T) => void] {
  const lsKey = `upref_${key}`;

  const [value, setValueState] = useState(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw !== null) return JSON.parse(raw) as T;
    } catch { /* ignore */ }
    return defaultValue;
  });

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(false);

  // Fetch from backend on mount
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;

    api.get<PreferenceResponse<T>>(`preferences/${key}/`)
      .then((data) => {
        if (cancelled || !mounted.current) return;
        if (data && data.value !== null && data.value !== undefined) {
          setValueState(data.value);
          try { localStorage.setItem(lsKey, JSON.stringify(data.value)); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* silently fall back to localStorage seed */ });

    return () => {
      cancelled = true;
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setValue = useCallback((newValue: T) => {
    setValueState(newValue);
    try { localStorage.setItem(lsKey, JSON.stringify(newValue)); } catch { /* ignore */ }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      api.put(`preferences/${key}/`, { value: newValue }).catch(() => { /* silent */ });
      debounceTimer.current = null;
    }, DEBOUNCE_MS);
  }, [key, lsKey]);

  return [value, setValue];
}
